/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { EditorAppearance } from '@atlaskit/editor-common/types';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { PromptEditorWrapper } from '../../components/PromptEditorWrapper/PromptEditorWrapper';
import type { PromptEditor } from '../../components/PromptEditorWrapper/PromptEditorWrapper';
import { Breakpoint, useElementBreakpoints } from '../../hooks/useElementBreakpoints';

import { useEscapeKey, useSubmitControls } from './hooks';
import messages from './messages';

type Props = {
	adfInput?: JSONDocNode;
	appearance: EditorAppearance;
	cancel: () => void;
	isAIFC?: boolean;
	onADFChange?: (value: JSONDocNode) => void;
	onTextChange: (value: string) => void;
	placeholder?: string;
	PromptEditor: PromptEditor;
	submit: () => void;
	textInput?: string;
};

const container = cssMap({
	padding: { paddingInline: token('space.200'), paddingBlock: token('space.150') },
});

export const PromptScreen = ({
	textInput,
	adfInput,
	onTextChange,
	onADFChange,
	submit,
	cancel,
	PromptEditor,
	appearance,
	isAIFC,
}: Props) => {
	const intl = useIntl();
	const screenRef = useRef<HTMLDivElement | null>(null);
	useEscapeKey(screenRef, cancel);
	const { onKeyDown } = useSubmitControls(submit, textInput);
	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints(Breakpoint.SmallChromeless);
	const placeholder =
		breakpoint <= Breakpoint.SmallChromeless
			? `${intl.formatMessage(messages.inlineCommentPromptPlaceholder)}...`
			: `${intl.formatMessage(messages.promptPlaceholder)}...`;

	const setRootContainer = (element: HTMLDivElement | null) => {
		screenRef.current = element;
		setBreakpointsElement(element);
	};

	return (
		<FloatingContainer
			ref={setRootContainer}
			data-testid="prompt-form-screen"
			disablePadding
			rainbowBorder={false}
			enableMaxHeight
			appearance={appearance}
			isAIFC={isAIFC}
		>
			<Inline xcss={container.padding}>
				<PromptEditorWrapper
					autoFocus
					PromptEditor={PromptEditor}
					type="user-input"
					defaultValue={adfInput}
					onInputChange={onTextChange}
					onADFChange={onADFChange}
					placeholder={placeholder}
					enableLinks
					onKeyDown={isAIFC ? undefined : onKeyDown}
					submit={submit}
					isAIFC={isAIFC}
				/>
			</Inline>
		</FloatingContainer>
	);
};
